import React, { useEffect, useState } from 'react';
import css from './css/AddStudentSurveyElementForm.scss';
import commonCss from './css/SurveyStyle.scss';
import classnames from 'classnames';
import Select from 'common/StyledSelect';
import Icon from 'SF/MaterialIcon';
import Checkbox from 'SF/Checkbox';
import { sortBy } from 'lodash';
import { getWord } from 'utils/tenant';
import useStarfishAPI from 'hooks/useStarfishAPI';
import PropTypes from 'prop-types';

const AddStudentSurveyElementForm = ({elementType, elementTypeDisplayName, elementData, onElementUpdate, errors, onErrorUpdate}) => {
    const [getQuestionsResponse, getQuestionsAPI] = useStarfishAPI({ isNewAPI: true });
    const [questions, setQuestions] = useState(null);

    useEffect(() => {
        (elementType === 'QUESTION') && getQuestionsAPI('../api/student-survey-questions', { method: 'GET' });
    }, [elementType]);

    useEffect(() => {
        if (getQuestionsResponse.success) {
            setQuestions(getQuestionsResponse.data.map(question => (
                {
                    value: question.id,
                    label: question.questionText,
                    questionType: question.questionType.value,
                    questionOptions: question.questionOptions

                }
            )));
        } else if (!getQuestionsResponse.pending && getQuestionsResponse.error) {
            onErrorUpdate({formError: `There was an error processing your request. \
                If this error continues to appear, please contact Starfish support.`});
        }
    }, [getQuestionsResponse]);

    const fieldError = (message) => (
        <div className={commonCss['field-error']} role='alert'><Icon icon='error' aria-hidden />{message}</div>
    );

    const handleQuestionChange = (questionData) => {
        onElementUpdate({
            templateQuestion: {
                questionId: questionData.value,
                questionText: questionData.label,
                questionType: questionData.questionType,
                questionOptions: questionData.questionOptions,
                required: elementData.templateQuestion?.required || false
            }
        });
        onErrorUpdate({fieldError: null});
    }

    const getQuestionFields = () => {
        const selectedQuestion = elementData.templateQuestion?.questionId ? {
            value: elementData.templateQuestion.questionId,
            label: elementData.templateQuestion.questionText
        } : null;

        return (
            <>
                <Select id='element-input'
                        aria-label='Question (Required)'
                        value={selectedQuestion}
                        onChange={e => handleQuestionChange(e)}
                        options={sortBy(questions, [question => question.label.toLowerCase()])}
                        isLoading={getQuestionsResponse.pending}
                        hasError={!!errors.fieldError}
                        placeholder='Question' />
                {errors.fieldError && fieldError(errors.fieldError)}

                <div className={css['required-checkbox']}>
                    <Checkbox aria-labelledby='question-required' ariaLabel=''
                              checked={elementData.templateQuestion?.required || false}
                              onChange={(checked) => onElementUpdate({templateQuestion: {required: checked}})} />
                    <label id='question-required' className={commonCss['instructional-text']}>
                        Make question required for this {getWord('studentSurvey', false)}
                    </label>
                </div>
            </>
        )
    };

    const getHeaderTextField = () => (
        <>
            <input
                id='element-input'
                className={classnames(css['input'], {[css['inputError']]: !!errors.fieldError})}
                type='text'
                value={elementData.elementText || ''}
                onChange={e => {onElementUpdate({elementText: e.target.value}); onErrorUpdate({fieldError: null});}}
                required />
            {errors.fieldError && fieldError(errors.fieldError)}
        </>
    );

    return (
        <div className={css['element-container']}>
            <label htmlFor='element-input' className={classnames(commonCss['field-label'], {[commonCss['error']]: !!errors.fieldError})}>
                {elementTypeDisplayName}
            </label>
            {elementType === 'QUESTION' ? getQuestionFields() : getHeaderTextField()}
        </div>
    );
};

AddStudentSurveyElementForm.propTypes = {
    elementType: PropTypes.string.isRequired,
    elementTypeDisplayName: PropTypes.string.isRequired,
    elementData: PropTypes.shape({
        elementText: PropTypes.string,
        templateQuestion: PropTypes.shape({
            questionId: PropTypes.number.isRequired,
            questionText: PropTypes.string.isRequired,
            questionType: PropTypes.string.isRequired,
            questionOptions: PropTypes.arrayOf(PropTypes.shape({
                text: PropTypes.string.isRequired,
                position: PropTypes.number.isRequired
            })).isRequired,
            required: PropTypes.bool.isRequired
        })
    }).isRequired,
    onElementUpdate: PropTypes.func.isRequired,
    errors: PropTypes.shape({
        formError: PropTypes.string,
        fieldError: PropTypes.string,
    }),
    onErrorUpdate: PropTypes.func.isRequired
};

export default AddStudentSurveyElementForm;
